// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { messages, useLanguage, useMessages, type Language } from '../i18n.js';
import { useEffect, useRef, useState } from 'react';
import { matchMediaOrNull } from '../browser.js';

export type Theme = 'light' | 'dark' | null;

const THEME_KEY = 'bilal-theme';

/** The stored value, or nothing; storage can refuse to answer and that is not an error. */
function storedTheme(): string | null {
  try {
    return globalThis.localStorage.getItem(THEME_KEY);
  } catch {
    return null;
  }
}

/**
 * The choice at startup: light unless the person has chosen otherwise (founder, 2026-09-20: "light
 * version the default"). Following the device is still offered and still sticks — it is written
 * down as `auto`, so "chosen automatic" and "has not chosen" stop being the same stored state.
 */
export function initialTheme(): Theme {
  const stored = storedTheme();
  if (stored === 'light' || stored === 'dark') return stored;
  if (stored === 'auto') return null;
  return 'light';
}

/** Applies the choice to the document and remembers it; null follows the device. */
export function applyTheme(theme: Theme): void {
  const root = globalThis.document.documentElement;
  try {
    globalThis.localStorage.setItem(THEME_KEY, theme ?? 'auto');
  } catch {
    /* a browser that refuses storage still gets the theme, just not the memory of it */
  }
  if (theme === null) {
    root.removeAttribute('data-theme');
    return;
  }
  root.setAttribute('data-theme', theme);
}

/** What the person is actually seeing right now, choice or system default. */
function effectiveDark(theme: Theme): boolean {
  if (theme !== null) return theme === 'dark';
  return matchMediaOrNull?.('(prefers-color-scheme: dark)').matches ?? false;
}

/**
 * The navigation rail: brand, new conversation, the one honest conversation entry (there is exactly
 * one resumable session per person today, and pretending to a history there is no backend for would
 * be a lie), memory shortcut, theme control and the development indicator.
 *
 * The rail sits after the conversation in the DOM so the keyboard reaches the composer first; the
 * stylesheet's `order` keeps it visually at the inline start. On narrow screens it becomes a drawer;
 * the parent owns the open state, this component owns what Escape and the backdrop do with it.
 */
const T = messages({
  ar: {
    today: 'اليوم',
    yesterday: 'أمس',
    thisWeek: 'هذا الأسبوع',
    older: 'أقدم',
    closeMenu: 'إغلاق القائمة',
    navigation: 'التنقل',
    appName: 'بلال',
    hideMenu: 'إخفاء القائمة',
    newConversation: '+ محادثة جديدة',
    conversations: 'المحادثات',
    searchConversations: 'البحث في المحادثات',
    searchPrompt: 'بحث…',
    previousConversations: 'المحادثات السابقة',
    noConversations: 'لا محادثات بعد.',
    noResults: 'لا نتائج للبحث.',
    untitled: 'محادثة جديدة',
    // Not "permanently" (ADR-042): it leaves the person's account at once and is kept for the
    // team's review for thirty days. The confirmation says so in full; the label must not lie.
    deleteConversation: (preview: string) => `حذف المحادثة: ${preview}`,
    deleteLabel: 'حذف المحادثة',
    explore: 'استكشاف المصادر',
    memory: 'الذاكرة والملف الشخصي',
    settings: 'الإعدادات',
    lightMode: 'الوضع الفاتح',
    darkMode: 'الوضع الداكن',
    signOut: 'تسجيل الخروج',
    accountMenu: 'حسابك وإعداداته',
    yourAccount: 'حسابك',
    beta: 'بيتا',
  },
  en: {
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This week',
    older: 'Older',
    closeMenu: 'Close the menu',
    navigation: 'Navigation',
    appName: 'Bilal',
    hideMenu: 'Hide the menu',
    newConversation: '+ New conversation',
    conversations: 'Conversations',
    searchConversations: 'Search the conversations',
    searchPrompt: 'Search…',
    previousConversations: 'Earlier conversations',
    noConversations: 'No conversations yet.',
    noResults: 'Nothing matches that search.',
    untitled: 'New conversation',
    deleteConversation: (preview: string) => `Delete the conversation: ${preview}`,
    deleteLabel: 'Delete the conversation',
    explore: 'Explore the sources',
    memory: 'Memory and profile',
    settings: 'Settings',
    lightMode: 'Light mode',
    darkMode: 'Dark mode',
    signOut: 'Sign out',
    accountMenu: 'Your account and its settings',
    yourAccount: 'Your account',
    beta: 'Beta',
  },
});

/** Today / yesterday / this week / older, from the conversation's own stamp against the clock. */
export function groupOf(
  createdAt: string,
  now: number = Date.now(),
  language: Language = 'ar',
): string {
  const words = T[language];
  const at = Date.parse(createdAt);
  if (Number.isNaN(at)) return words.older;
  const day = 24 * 60 * 60 * 1000;
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  if (at >= startOfToday.getTime()) return words.today;
  if (at >= startOfToday.getTime() - day) return words.yesterday;
  if (at >= startOfToday.getTime() - 6 * day) return words.thisWeek;
  return words.older;
}

/**
 * Deletion, drawn rather than typed. The emoji 🗑 renders as a full-colour picture that ignores the
 * colour the control sets, so the rail's one destructive affordance arrived in a palette of its own
 * — and it turns red on hover, which a picture cannot do.
 */
function TrashIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="glyph" aria-hidden="true" focusable="false">
      <path
        d="M4 7h16M10 4h4M9.5 7.5v9M14.5 7.5v9M6.5 7l.8 12a2 2 0 0 0 2 1.9h5.4a2 2 0 0 0 2-1.9l.8-12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The account's own initial for the rail's avatar; a question mark when there is nothing to take. */
function initialOf(name: string | null, email: string | null): string {
  const source = (name ?? '').trim().length > 0 ? (name ?? '').trim() : (email ?? '');
  return source.slice(0, 1).toUpperCase() || '·';
}

export function Sidebar({
  open,
  onClose,
  onCollapse,
  onNewConversation,
  onShowMemory,
  onShowExplore,
  onShowSettings,
  accountLabel,
  accountName,
  theme,
  onToggleTheme,
  liveIndicator,
  onSignOut,
  conversations,
  activeConversationId,
  onOpenConversation,
  onDeleteConversation,
}: {
  /** Drawer state on narrow screens; ignored by the fixed rail on wide ones. */
  readonly open: boolean;
  readonly onClose: () => void;
  /** Folds the rail away on wide screens; the top bar's menu button brings it back. */
  readonly onCollapse: () => void;
  readonly onNewConversation: () => void;
  readonly onShowMemory: () => void;
  readonly onShowExplore: () => void;
  readonly onShowSettings: () => void;
  /** Which account this is, shown under the foot; null when unknown. */
  readonly accountLabel: string | null;
  /** The name the person chose (ADR-034); drawn as an initial chip and the name when known. */
  readonly accountName?: string | null;
  readonly theme: Theme;
  readonly onToggleTheme: () => void;
  /** Optional operator note under the rail foot; null hides it entirely. */
  readonly liveIndicator: string | null;
  readonly onSignOut: () => void;
  /** The caller's conversations, newest first; empty until the first turn lands anywhere. */
  readonly conversations: readonly {
    readonly id: string;
    readonly preview: string;
    readonly createdAt: string;
  }[];
  readonly activeConversationId: string | null;
  readonly onOpenConversation: (conversationId: string) => void;
  /** Permanent erasure; the parent owns the confirmation, this component only asks for it. */
  readonly onDeleteConversation: (conversationId: string) => void;
}): React.JSX.Element {
  const t = useMessages(T);
  const language = useLanguage();
  const firstAction = useRef<HTMLButtonElement>(null);
  // Search filters what is shown, never what exists; it appears only once there is enough to lose.
  const [query, setQuery] = useState('');
  const visibleConversations =
    query.trim().length === 0
      ? conversations
      : conversations.filter((conversation) => conversation.preview.includes(query.trim()));

  // An opened drawer takes the keyboard and gives it back on close; Escape closes it.
  useEffect(() => {
    if (!open) return;
    firstAction.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    globalThis.addEventListener('keydown', onKey);
    return () => {
      globalThis.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const dark = effectiveDark(theme);
  const [menuOpen, setMenuOpen] = useState(false);
  const menu = useRef<HTMLDivElement | null>(null);
  const accountButton = useRef<HTMLButtonElement | null>(null);
  /**
   * Every item closes the menu and then does its thing, so nothing is left hanging open. The
   * keyboard goes back to the account button *before* the act runs: what an item opens is often a
   * dialog, and a dialog returns focus to whatever was focused when it opened — which would
   * otherwise be a menu item that no longer exists, stranding the keyboard on the document body.
   */
  const closeThen = (act: () => void): void => {
    setMenuOpen(false);
    accountButton.current?.focus();
    act();
  };
  // A menu that only closes on its own items is a menu that follows you around: Escape closes it,
  // a click anywhere else closes it, and the keyboard goes back to the control that opened it.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      accountButton.current?.focus();
    };
    const onPointer = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (menu.current?.contains(target) === true) return;
      if (accountButton.current?.contains(target) === true) return;
      setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [menuOpen]);
  return (
    <>
      {open && (
        <button
          type="button"
          className="rail-backdrop"
          aria-label={t.closeMenu}
          onClick={onClose}
        />
      )}
      <aside className="rail" aria-label={t.navigation}>
        <div className="rail-brand">
          <span className="logo-plate">
            <img src="/brand/emblem.svg" alt={t.appName} className="brand-light" />
            <img src="/brand/emblem-jass.svg" alt={t.appName} className="brand-dark" />
          </span>
          <h1 className="visually-hidden">{t.appName}</h1>
          <button
            type="button"
            className="collapse"
            aria-label={t.hideMenu}
            title={t.hideMenu}
            onClick={onCollapse}
          >
            ⇥
          </button>
        </div>
        <button type="button" ref={firstAction} className="new-chat" onClick={onNewConversation}>
          {t.newConversation}
        </button>
        <p className="rail-section">{t.conversations}</p>
        {conversations.length > 3 && (
          <input
            type="search"
            className="conversation-search"
            aria-label={t.searchConversations}
            placeholder={t.searchPrompt}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        )}
        <nav aria-label={t.previousConversations} className="conversation-list">
          {conversations.length === 0 && <p className="conversation-empty">{t.noConversations}</p>}
          {visibleConversations.length === 0 && conversations.length > 0 && (
            <p className="conversation-empty">{t.noResults}</p>
          )}
          {visibleConversations.map((conversation, index) => (
            <div key={conversation.id} className="conversation-row-group">
              {/* Date groups as the canvas draws them; only when nothing is being searched. */}
              {query.trim().length === 0 &&
                groupOf(conversation.createdAt, Date.now(), language) !==
                  (index === 0
                    ? null
                    : groupOf(
                        visibleConversations[index - 1]?.createdAt ?? '',
                        Date.now(),
                        language,
                      )) && (
                  <p className="rail-group">
                    {groupOf(conversation.createdAt, Date.now(), language)}
                  </p>
                )}
              <div className="conversation-row">
                <button
                  type="button"
                  className={
                    conversation.id === activeConversationId
                      ? 'conversation-entry active'
                      : 'conversation-entry'
                  }
                  dir="auto"
                  onClick={() => {
                    onOpenConversation(conversation.id);
                  }}
                >
                  {conversation.preview.length > 0 ? conversation.preview : t.untitled}
                </button>
                <button
                  type="button"
                  className="conversation-delete"
                  aria-label={t.deleteConversation(
                    conversation.preview.length > 0 ? conversation.preview : t.untitled,
                  )}
                  title={t.deleteLabel}
                  onClick={() => {
                    onDeleteConversation(conversation.id);
                  }}
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
          ))}
        </nav>
        {/* One row, and everything else behind it (founder, 2026-09-13: "most of these buttons could
            be inside the settings, hidden more"). The rail is for conversations; the account and
            what belongs to it open from here, and the two destructive actions live only in the
            settings, where an action that cannot be undone belongs. */}
        <div className="rail-foot">
          <button
            type="button"
            className="rail-account-button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={t.accountMenu}
            title={accountLabel ?? undefined}
            ref={accountButton}
            onClick={() => {
              setMenuOpen((current) => !current);
            }}
          >
            <span className="rail-avatar" aria-hidden="true">
              {initialOf(accountName ?? null, accountLabel)}
            </span>
            <span className="rail-whose">
              <span className="rail-name">{accountName ?? accountLabel ?? t.yourAccount}</span>
              <span className="beta-chip">
                {t.beta}
                {liveIndicator === null ? '' : ` · ${liveIndicator}`}
              </span>
            </span>
            <span className="rail-caret" aria-hidden="true">
              ⌄
            </span>
          </button>
          {menuOpen && (
            <div className="rail-menu" role="menu" aria-label={t.accountMenu} ref={menu}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeThen(onShowMemory);
                }}
              >
                {t.memory}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeThen(onShowExplore);
                }}
              >
                {t.explore}
              </button>
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={dark}
                onClick={() => {
                  closeThen(onToggleTheme);
                }}
              >
                {dark ? t.lightMode : t.darkMode}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeThen(onShowSettings);
                }}
              >
                {t.settings}
              </button>
              <button
                type="button"
                role="menuitem"
                className="sign-out"
                onClick={() => {
                  closeThen(onSignOut);
                }}
              >
                {t.signOut}
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
